import { Button } from "@client/components/utils/Button";
import { withErrorBoundary } from "@client/components/utils/ErrorBoundary";
import { StateContext } from "@client/pages/round/Round";
import { trpc } from "@client/trpc";
import userIsAdmin from "@client/utils/userIsAdmin";
import type { ActionMessage } from "@engine/core";
import { isUserFunder } from "@engine/round/accessors";
import type { RoundAggregate } from "@engine/round/aggregate";
import { useContext, useEffect, useState } from "react";
import { Redirect } from "wouter";
import { ShowEventLogItem } from "./ShowEventLogItem";
import { getEventLogItems, type ActionLogItem } from "./getEventLogItems";

const ITEMS_PER_PAGE = 50;

async function getActions(
  aggregateId: string,
  [startVersion, endVersion]: [number, number],
): Promise<ActionMessage[]> {
  if (startVersion > endVersion) {
    return [];
  }

  const queryResult = await trpc().getAggregateActionsByVersion.query({
    roundId: aggregateId,
    versions: [[startVersion, endVersion]],
  });
  if (queryResult === "round-member-not-found") {
    throw new Error("User is not a member of this round");
  }

  const result: ActionMessage[] = [];
  for (let i = startVersion; i <= endVersion; i++) {
    result.push(queryResult[i]);
  }
  return result;
}

export const Logs = withErrorBoundary(
  ({
    round,
    lastActionNumber,
  }: {
    round: RoundAggregate;
    lastActionNumber: number;
  }) => {
    const [items, setItems] = useState<ActionLogItem[]>([]);

    // Counting back from the end of the list
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(false);

    const beginning = Math.max(
      0,
      lastActionNumber - (page + 1) * ITEMS_PER_PAGE,
    );
    const end = lastActionNumber - page * ITEMS_PER_PAGE;
    const previousDisabled = beginning === 0;

    const context = useContext(StateContext);
    const { actingAsUserId: userId } = context;

    const isAdmin = userIsAdmin(userId, context);
    const isFunder = isUserFunder(round, userId);
    if (!isAdmin && !isFunder) {
      return <Redirect to="/" />;
    }

    useEffect(() => {
      const loadActions = async () => {
        setLoading(true);
        const actions = await getActions(round.id, [beginning, end - 1]);
        const actionLogItems = getEventLogItems(actions).reverse();
        setItems(actionLogItems);
        setLoading(false);
      };
      void loadActions();
    }, [round.id, beginning, end]);

    return (
      <div className="flex flex-col justify-center mx-auto mt-10">
        <div className="flex flex-row gap-4">
          <Button
            disabled={previousDisabled}
            onClick={() => {
              setLoading(true);
              setPage(page + 1);
            }}
          >
            Previous
          </Button>
          <Button
            disabled={page === 0}
            onClick={() => {
              setLoading(true);
              setPage(page - 1);
            }}
          >
            Next
          </Button>
          <span className="text-sm text-gray-500 flex items-center">
            {beginning + 1} - {end} of {lastActionNumber}
          </span>
        </div>
        <div className="w-[800px] overflow-y-auto grow">
          {loading ? (
            <div className="text-center py-4">Loading...</div>
          ) : items.length > 0 ? (
            items.map((logItem, i) => (
              <ShowEventLogItem key={i} round={round} item={logItem} />
            ))
          ) : (
            <div className="text-center py-4">No logs available</div>
          )}
        </div>
      </div>
    );
  },
);
